'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { subscribeToTable } from '@/lib/supabase/realtime-registry';
import { api } from '@/lib/utils/api';
import { useAuthStore } from '@/stores/auth-store';

export type PlantStato = 'ok' | 'warn' | 'crit';
export type PlantTipo = 'ci' | 'campo_terra' | 'tetto';

export interface PlantCard {
	id: string;
	nome: string;
	localita: string | null;
	tipo: PlantTipo | null;
	potenzaKwp: number;
	liveKw: number;
	pr: number | null;
	prTarget: number | null;
	inverterTotali: number;
	inverterOk: number;
	stringheTotali: number;
	anomalieAperte: number;
	stato: PlantStato;
	/** Almeno un inverter ha mandato un campione negli ultimi minuti (INB-333). */
	inLinea: boolean;
	energiaGiornoKwh: number | null;
	/** Parte stimata di `energiaGiornoKwh` (INB-354). */
	energiaGiornoStimataKwh?: number;
}

export interface PortfolioKpi {
	impiantiTotali: number;
	impiantiOnline: number;
	potenzaTotaleKwp: number;
	inverterMonitorati: number;
	stringheMonitorate: number;
	prMedio: number | null;
	disponibilita: number | null;
	liveKw: number;
	energiaPeriodoKwh: number;
	/** Parte stimata di `energiaPeriodoKwh` (INB-354). */
	energiaPeriodoStimataKwh?: number;
	composizione: { tipo: PlantTipo | null; count: number }[];
	curvaOggi: { ora: string; ac: number; dc: number; nominale?: number }[];
	/** Giorno a cui si riferisce `curvaOggi`, `YYYY-MM-DD`. */
	giornoCurva: string;
	selfConsumptionPct: number | null;
	mttrMedioOre: number | null;
	/** Variazione % di `energiaPeriodoKwh` sul periodo precedente di pari durata. `null` senza confronto sensato. */
	energiaPeriodoDeltaPct: number | null;
}

interface PlantsPayload {
	plants: PlantCard[];
	portfolio: PortfolioKpi | null;
}

const EMPTY_PLANTS: PlantCard[] = [];

/** React Query key for the plants overview of one org (optionally a `YYYY-MM` month). */
export const plantsKey = (orgId: string | null | undefined, month?: string) =>
	['plants', orgId, month ?? null] as const;

/**
 * Panoramica impianti: le card più i KPI di portfolio (`GET /api/plants`,
 * `GET /api/plants/portfolio`).
 *
 * `month` in formato `YYYY-MM` sceglie il periodo dei KPI; senza, è il mese in
 * corso fino a oggi.
 *
 * Il canale realtime è su `plant_inverters` e non sulla telemetria: lo stato di
 * un inverter cambia di rado ed è ciò che colora i pallini, mentre i campioni
 * arrivano al minuto e replicarli via websocket saturerebbe il canale per un
 * dato che il refetch periodico porta comunque.
 *
 * ADR-006: backed by React Query — cached e deduplicato tra pannelli e
 * navigazioni; il canale `plant_inverters` invalida l'entry.
 */
export function usePlants(orgId: string | null | undefined, month?: string) {
	const queryClient = useQueryClient();
	const loaded = useAuthStore((s) => s.loaded);
	const enabled = loaded && !!orgId;

	const { data, isLoading, error, refetch: rqRefetch } = useQuery({
		queryKey: plantsKey(orgId, month),
		enabled,
		refetchInterval: 60_000,
		queryFn: async (): Promise<PlantsPayload> => {
			const [cards, kpi] = await Promise.all([
				api.get<PlantCard[]>('/api/plants', { orgId: orgId as string }),
				api.get<PortfolioKpi>(
					'/api/plants/portfolio',
					month ? { orgId: orgId as string, month } : { orgId: orgId as string },
				),
			]);
			return { plants: Array.isArray(cards) ? cards : [], portfolio: kpi ?? null };
		},
	});

	const refetch = useCallback(async () => {
		await rqRefetch();
	}, [rqRefetch]);

	useEffect(() => {
		if (!orgId) return;
		// Refetch e non patch locale: la card è un aggregato (live, PR, anomalie,
		// energia del giorno) che la riga dell'inverter da sola non ricostruisce.
		return subscribeToTable(
			`plant-inverters-realtime-${orgId}`,
			{ event: '*', schema: 'public', table: 'plant_inverters' },
			() => {
				queryClient.invalidateQueries({ queryKey: plantsKey(orgId, month) });
			},
		);
	}, [orgId, month, queryClient]);

	return {
		plants: data?.plants ?? EMPTY_PLANTS,
		portfolio: data?.portfolio ?? null,
		isLoading: enabled ? isLoading : false,
		error: error
			? error instanceof Error
				? error.message
				: 'Errore nel caricamento degli impianti'
			: null,
		refetch,
	};
}
